// Stats › Proposals & quotes (workflow D1, mockups-v3 ?view=proposals) —
// the send register band between the quarter goals and Revenue by month.
// Stats owns the fetch (it rides the page's own refresh) and the Log sent
// form; this band filters, summarises and lists what it is given.

import { WonProjectAction } from './WonProjectAction';
import { useMemo, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  DOCUMENT_SEND_OUTCOMES,
  DOCUMENT_SEND_OUTCOME_LABELS,
  type DocumentSendKind,
  type DocumentSendListItem,
  type DocumentSendOutcome,
} from '../../shared/types';
import {
  amountOf,
  groupByMonth,
  compactAmountOf,
  kindLabel,
  matchesSendQuery,
  monthTitle,
  OUTCOME_TONE,
  sendOpenTarget,
  summarizeSends,
} from '../lib/documentSends';
import { useIsMobile } from '../lib/useIsMobile';
import {
  FilterBar,
  formatDayMonth,
  formatMoney,
  formatProjectNumber,
  Group,
  Pill,
  SearchBox,
  SkeletonRows,
  Tabs,
  Vital,
  Vitals,
} from './primitives';

type KindTab = 'all' | DocumentSendKind;

const KIND_TABS: readonly { value: KindTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'proposal', label: 'Proposals' },
  { value: 'quote', label: 'Quotes' },
];

const OUTCOME_ITEMS = DOCUMENT_SEND_OUTCOMES.map((o) => ({ value: o, label: DOCUMENT_SEND_OUTCOME_LABELS[o] }));

/** "Northgate Health · SS-2026-0153 · Lead" — client, ref, then the link. */
function contextOf(s: DocumentSendListItem): string {
  const bits = [s.client_label ?? '—'];
  if (s.ref) bits.push(s.ref);
  if (s.project_id) {
    const num = formatProjectNumber(s.project_number);
    bits.push(num ? `Project ${num}` : 'Project');
  } else if (s.lead_id) bits.push('Lead');
  return bits.join(' · ');
}

const stop = (e: MouseEvent) => e.stopPropagation();

function OpenLink({ send }: { send: DocumentSendListItem }) {
  const target = sendOpenTarget(send);
  if (!target) return null;
  return 'internal' in target ? (
    <Link to={target.internal} onClick={stop} style={{ color: 'inherit' }}>
      Open ↗
    </Link>
  ) : (
    <a href={target.external} target="_blank" rel="noreferrer" onClick={stop} style={{ color: 'inherit' }}>
      Open ↗
    </a>
  );
}

export function DocumentSendsBand({
  sends,
  sendsFailed = false,
  periodLabel,
  mobileRangeLabel,
  onLogSent,
  onEdit,
}: {
  /** null while the first fetch is in flight. */
  sends: DocumentSendListItem[] | null;
  /** The last sends fetch failed; shown instead of a skeleton or empty state. */
  sendsFailed?: boolean;
  /** "FY27" / "FY27 Q2" / the custom range — the band head's count prefix. */
  periodLabel: string;
  /** Phone head: "FY27 · Jul – Oct". */
  mobileRangeLabel: string;
  onLogSent: () => void;
  onEdit: (send: DocumentSendListItem) => void;
}) {
  const isMobile = useIsMobile();
  const [kindTab, setKindTab] = useState<KindTab>('all');
  const [filters, setFilters] = useState<Record<string, string | null>>({ outcome: null, client: null });
  const [query, setQuery] = useState('');

  const inRange = sends ?? [];
  const summary = summarizeSends(inRange);
  const clientItems = useMemo(
    () =>
      Array.from(new Set((sends ?? []).map((s) => s.client_label).filter((c): c is string => Boolean(c))))
        .sort((a, b) => a.localeCompare(b))
        .map((c) => ({ value: c, label: c })),
    [sends],
  );

  const visible = inRange.filter(
    (s) =>
      (kindTab === 'all' || s.kind === kindTab) &&
      (isMobile || !filters.outcome || s.outcome === (filters.outcome as DocumentSendOutcome)) &&
      (isMobile || !filters.client || s.client_label === filters.client) &&
      (isMobile || matchesSendQuery(s, query)),
  );
  const months = groupByMonth(visible);

  const vitals = (
    <div className="sends-vitals">
      <Vitals>
        <Vital
          label="Sent"
          value={summary.sent}
          tone="accent"
          sub={
            isMobile
              ? `${summary.pending} awaiting reply · ${summary.onHold} on hold`
              : `${summary.proposals} proposal${summary.proposals === 1 ? '' : 's'} · ${summary.quotes} quote${summary.quotes === 1 ? '' : 's'}`
          }
        />
        <Vital label="Won" value={summary.won} tone="ok" sub={`${summary.wonPct}% of decided`} />
        {!isMobile && <Vital label="Awaiting reply" value={summary.pending} tone="gold" sub={`${summary.onHold} on hold`} />}
        <Vital label="Value sent" value={formatMoney(summary.valueSentCents)} tone="gold" sub="ex GST · low end" />
        <Vital label="Value won" value={formatMoney(summary.valueWonCents)} tone="gold" sub="ex GST" />
      </Vitals>
    </div>
  );

  const failed = <div className="empty-note">Couldn't load the sends. Refresh to try again.</div>;
  const filtering = kindTab !== 'all' || query.trim() !== '' || Object.values(filters).some((v) => v !== null);
  const empty = sends !== null && visible.length === 0 && (
    <div className="empty-note">
      {sends.length > 0 && filtering ? 'Nothing matches these filters.' : 'Nothing sent in this range yet.'}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="group-head" style={{ border: 'none', padding: '22px 0 9px' }}>
          <span className="group-title">Proposals & quotes</span>
          <span className="group-count">{mobileRangeLabel}</span>
        </div>
        {vitals}
        <div style={{ margin: '14px -20px 0' }}>
          <Tabs options={KIND_TABS} value={kindTab} onChange={setKindTab} />
        </div>
        {sends === null ? (
          sendsFailed ? failed : <SkeletonRows rows={3} />
        ) : (
          <div className="sends-list" style={{ margin: '0 -20px' }}>
            {months.map((m) => (
              <section key={m.month}>
                <div className="sends-m-group">
                  <span>{monthTitle(m.month)}</span>
                  <span className="n">{m.sends.length}</span>
                </div>
                {m.sends.map((s) => (
                  <div key={s.id} className="project-list-row compact">
                    <button type="button" className="project-list-open" onClick={() => onEdit(s)}>
                      <span className="prow-main">
                        <span className="prow-title">{s.title}</span>
                        <span className="prow-meta">
                          {kindLabel(s)} · {s.client_label ?? '—'} · {formatDayMonth(s.sent_date)} ·{' '}
                          <span className="gold">{compactAmountOf(s)}</span>
                        </span>
                      </span>
                      <span className="prow-right">
                        <Pill tone={OUTCOME_TONE[s.outcome]}>{DOCUMENT_SEND_OUTCOME_LABELS[s.outcome]}</Pill>
                      </span>
                    </button>
                    <div className="won-mobile-action"><WonProjectAction send={s}/></div>
                  </div>
                ))}
              </section>
            ))}
            {empty && <div style={{ padding: '0 20px' }}>{empty}</div>}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="group-head" style={{ border: 'none', padding: '26px 0 9px' }}>
        <span className="group-title">Proposals & quotes</span>
        <span className="group-count">
          {periodLabel} · {summary.sent} sent
        </span>
        <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={onLogSent}>
          + Log sent
        </button>
      </div>
      {vitals}
      <div className="sends-toolbar">
        <Tabs options={KIND_TABS} value={kindTab} onChange={setKindTab} />
        <FilterBar
          fields={[
            { key: 'outcome', label: 'Outcome', items: OUTCOME_ITEMS },
            { key: 'client', label: 'Client', items: clientItems, searchable: true },
          ]}
          active={filters}
          onChange={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        />
        <SearchBox placeholder="Search titles, clients, refs…" value={query} onChange={setQuery} />
      </div>
      {sends === null ? (
        sendsFailed ? failed : <SkeletonRows rows={4} />
      ) : (
        months.map((m) => (
          <Group key={m.month} title={monthTitle(m.month)} count={m.sends.length}>
            {m.sends.map((s) => (
              <div
                key={s.id}
                className="row sends-row"
                role="button"
                tabIndex={0}
                onClick={() => onEdit(s)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEdit(s);
                  }
                }}
              >
                <span className="num hi sends-date">{formatDayMonth(s.sent_date)}</span>
                <Pill tone={s.kind === 'proposal' ? 'accent' : undefined}>{kindLabel(s)}</Pill>
                <span className="row-title" title={s.title}>
                  {s.title}
                </span>
                <span className="row-context" title={contextOf(s)}>
                  {contextOf(s)}
                </span>
                <span className="row-spacer" />
                {s.date_estimated === 1 && (
                  <span
                    className="pill est"
                    title="Sent date estimated from when the proposal was published — click to confirm"
                  >
                    date est.
                  </span>
                )}
                <span className="row-meta gold sends-amount">{amountOf(s)}</span>
                <Pill tone={OUTCOME_TONE[s.outcome]}>{DOCUMENT_SEND_OUTCOME_LABELS[s.outcome]}</Pill>
                <WonProjectAction send={s}/>
                <span className="col-view">
                  <OpenLink send={s} />
                </span>
              </div>
            ))}
          </Group>
        ))
      )}
      {empty}
    </>
  );
}
